import { create } from "zustand";
import {
  getPublicHolidays,
  getOptionalHolidays,
  getSelectedOptionalHolidays,
  submitSelectedHolidays,
  cancelOptionalHolidays,
  getHolidayConfig,
  getAvailableYears,
  checkDataForYear
} from "@/services/EmployeeService/HolidayService";

const useHolidayStore = create((set, get) => ({
  publicHolidays: null,
  optionalHolidays: null,
  selectedOptionalHolidays: [],
  availableYears: [],
  currentFyYear: null,
  loading: false,
  error: null,
  maxOptionalSelections: 4,

  // Fetch holiday config (dynamic max optional selections)
  fetchHolidayConfig: async (fyYear = null) => {
    try {
      console.log(`[HolidayStore] Fetching holiday config for FY Year: ${fyYear}`);
      const response = await getHolidayConfig(fyYear);
      console.log(`[HolidayStore] Holiday config API response:`, response);
      if (response.success && response.data) {
        console.log(`[HolidayStore] Setting maxOptionalSelections to: ${response.data.max_optional_holidays}`);
        set({ maxOptionalSelections: response.data.max_optional_holidays });
      } else {
        console.warn(`[HolidayStore] Failed to fetch config or no data received`);
      }
    } catch (error) {
      console.error("Fetch Holiday Config Error:", error);
    }
  },

  // Fetch available years
  fetchAvailableYears: async () => {
    try {
      const response = await getAvailableYears();
      if (response.success) {
        set({ availableYears: response.data });
      }
    } catch (error) {
      console.error("Fetch Available Years Error:", error);
    }
  },

  // Check if data exists for a year
  checkYearData: async (fyYear) => {
    try {
      const response = await checkDataForYear(fyYear);
      return response.success ? response.data.hasData : false;
    } catch (error) {
      console.error("Check Year Data Error:", error);
      return false;
    }
  },

  // Fetch public holidays (with optional year)
  fetchPublicHolidays: async (fyYear = null) => {
    try {
      set({ loading: true, error: null, currentFyYear: fyYear });
      const response = await getPublicHolidays(fyYear);
      console.log("Public Holidays API Response:", response);

      if (response.success) {
        set({ publicHolidays: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch public holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch Public Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch optional holidays (with optional year)
  fetchOptionalHolidays: async (fyYear = null) => {
    try {
      set({ loading: true, error: null, currentFyYear: fyYear });
      const response = await getOptionalHolidays(fyYear);
      console.log("Optional Holidays API Response:", response);

      if (response.success) {
        set({ optionalHolidays: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch optional holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch Optional Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch employee's selected optional holidays
  fetchSelectedOptionalHolidays: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getSelectedOptionalHolidays();
      console.log("Selected Optional Holidays API Response:", response);

      if (response.success) {
        set({ selectedOptionalHolidays: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch selected holidays', loading: false });
      }
    } catch (error) {
      console.error("Fetch Selected Optional Holidays Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Submit selected optional holidays
  submitHolidaySelection: async (holidayIds) => {
    try {
      set({ loading: true, error: null });
      const response = await submitSelectedHolidays(holidayIds);
      console.log("Submit Holiday Selection API Response:", response);

      if (response.success) {
        set({ selectedOptionalHolidays: holidayIds, loading: false });
        return { success: true };
      } else {
        set({ error: response.error?.message || 'Failed to submit holiday selection', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Submit Holiday Selection Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Cancel one or more optional holidays
  cancelOptionalHolidays: async (holidayIds) => {
    try {
      set({ loading: true, error: null });
      const response = await cancelOptionalHolidays(holidayIds);
      console.log("Cancel Optional Holidays API Response:", response);

      if (response.success) {
        // Update the selected holidays list with the remaining holidays from the API response
        const remainingHolidays = response.data?.remainingHolidays || [];
        set({ selectedOptionalHolidays: remainingHolidays, loading: false });
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to cancel holidays', loading: false });
        return { success: false, error: response.error?.message };
      }
    } catch (error) {
      console.error("Cancel Optional Holidays Error:", error);
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  // Reset errors
  resetErrors: () => set({ error: null }),

  // Reset all data
  resetHolidayData: () => set({
    publicHolidays: null,
    optionalHolidays: null,
    selectedOptionalHolidays: [],
    loading: false,
    error: null
  }),
}));

export default useHolidayStore;
